import React, { useState, useEffect } from 'react';
import '../../styles/components/TemplatesList.scss';
import { getSpecialRules, createSpecialRule, updateSpecialRule, deleteSpecialRule, applySpecialRule } from '../../api/appSpecialRulesApi.js';
import { getAppTemplateDomainById } from '../../api/appTemplatesApi.js';
import { getAppKeywordDomainById, getAppKeywords } from '../../api/appKeyWordsApi.js';
import { useLoader } from '../../context/LoaderContext.js';
import { Box, Card, CardContent, Typography, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import { useDomain } from '../../context/DomainContext.js';
import SpecialRulesDataGrid from './SpecialRulesDataGrid.js';
import DeleteModal from '../DeleteModal.js';
import SpecialRuleModal from './SpecialRuleModal.js';

function SpecialRulesList() {
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openApplyModal, setOpenApplyModal] = useState(false);
  const [ruleToApply, setRuleToApply] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, text: '', error: false });
  const { showLoader, hideLoader } = useLoader();
  const { selectedDomain } = useDomain();

  const loadRules = () => {
    showLoader();
    getSpecialRules()
      .then(response => {
        setRules(response.data);
      })
      .finally(() => { hideLoader() })
      .catch(error => console.error('Error fetching special rules:', error));
  };

  const loadTemplates = () => {
    getAppTemplateDomainById(selectedDomain)
      .then(response => {
        setTemplates(response.data);
      })
      .catch(error => console.error('Error fetching templates:', error));
  };

  const loadKeywords = () => {
    if (!selectedDomain) return;
    getAppKeywordDomainById(selectedDomain)
      .then(response => {
        if (response.data && response.data.length > 0) {
          setKeywords(response.data);
        } else {
          getAppKeywords()
            .then(res => setKeywords(res.data || []))
            .catch(() => setKeywords([]));
        }
      })
      .catch(() => {
        getAppKeywords()
          .then(res => setKeywords(res.data || []))
          .catch(() => setKeywords([]));
      });
  };

  const handleOpenDeleteModal = (rule) => {
    setSelectedRule(rule);
    setOpenDeleteModal(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenDeleteModal(false);
    setSelectedRule(null);
  };

  const handleOpenModal = (rule = null) => {
    setSelectedRule(rule);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRule(null);
  };

  const handleSaveRule = async (data, isNew) => {
    try {
      showLoader();
      let ruleMessage = isNew ? 'La regla se creó correctamente' : 'La regla se guardó correctamente';
      isNew ? await createSpecialRule({ ...data, domain_id: selectedDomain }) : await updateSpecialRule(selectedRule.id, data);

      setOpenModal(false);
      setSelectedRule(null);
      setOpenSnackbar({ open: true, text: ruleMessage, error: false });
      loadRules();
    } catch (error) {
      console.error('Error guardando la regla', error);
      let errorMsg = error.type === 'duplicate' ? 'La regla ya existe.' : 'Error guardando la regla, intente nuevamente.';
      setOpenSnackbar({ open: true, text: errorMsg, error: true });
    } finally {
      hideLoader();
    }
  };

  const handleDeleteRule = async () => {
    try {
      showLoader();
      await deleteSpecialRule(selectedRule.id);
      setOpenSnackbar({ open: true, text: 'La regla se ha eliminado', error: false });
      loadRules();
    } catch (error) {
      console.error('Error eliminando la regla:', error);
      setOpenSnackbar({ open: true, text: 'Error eliminando la regla, intente nuevamente', error: true });
    } finally {
      setOpenDeleteModal(false);
      setSelectedRule(null);
      hideLoader();
    }
  };

  const handleOpenApplyModal = (rule) => {
    setRuleToApply(rule);
    setOpenApplyModal(true);
  };

  const handleCloseApplyModal = () => {
    setOpenApplyModal(false);
    setRuleToApply(null);
  };

  const handleApplyRule = async () => {
    if (!ruleToApply?.id) return;
    try {
      showLoader();
      const result = await applySpecialRule(ruleToApply.id);
      let displayMsg;
      if (result.tasksCreated !== undefined) {
        if (result.tasksCreated === 0) {
          displayMsg = result.message || 'No se encontraron usuarios coincidentes para esta regla';
        } else if (result.tasksCreated === 1) {
          displayMsg = 'Se creó exitosamente 1 tarea para la actualización de firma';
        } else {
          displayMsg = `Se crearon exitosamente ${result.tasksCreated} tareas para la actualización de firma`;
        }
      } else {
        displayMsg = result.message || 'Regla especial aplicada exitosamente';
      }

      setOpenSnackbar({
        open: true,
        text: displayMsg,
        error: false
      });
    } catch (error) {
      console.error('Error aplicando la regla:', error);
      setOpenSnackbar({
        open: true,
        text: error.message || 'Error al aplicar la regla, intente nuevamente',
        error: true
      });
    } finally {
      hideLoader();
      setOpenApplyModal(false);
      setRuleToApply(null);
    }
  };

  useEffect(() => {
    if (selectedDomain) {
      loadRules();
      loadTemplates();
      loadKeywords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 234px)',
      pt: 3,
      pb: 1
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h5" component="h1">
          Reglas especiales
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AssignmentIcon />}
          onClick={() => handleOpenModal()}
        >
          Nueva Regla Especial
        </Button>
      </Box>

      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, p: 0, '&:last-child': { pb: 0 } }}>
          <SpecialRulesDataGrid
            rules={rules}
            handleOpenDeleteModal={handleOpenDeleteModal}
            handleOpenModal={handleOpenModal}
            handleOpenApplyModal={handleOpenApplyModal}
          />
        </CardContent>
      </Card>

      <SpecialRuleModal
        openModal={openModal}
        selectedRule={selectedRule}
        handleCloseModal={handleCloseModal}
        handleSaveRule={handleSaveRule}
        templates={templates}
        keywords={keywords}
        domainId={selectedDomain}
      />

      <DeleteModal
        openConfirmModal={openDeleteModal}
        selectedItem={selectedRule}
        handleCloseConfirmModal={handleCloseConfirmModal}
        onDelete={handleDeleteRule}
        message='¿Estás seguro de que quieres eliminar esta regla especial?'
      />

      <Dialog
        open={openApplyModal}
        onClose={handleCloseApplyModal}
        aria-labelledby="apply-rule-dialog-title"
      >
        <DialogTitle id="apply-rule-dialog-title">
          Aplicar Regla Especial en Línea
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas aplicar la regla <strong>"{ruleToApply?.name}"</strong> ahora?
            <br /><br />
            Esto asignará la plantilla a los usuarios correspondientes y encolará la actualización inmediata de firmas en Gmail para los usuarios activos que coincidan con la regla.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApplyModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleApplyRule} color="primary" variant="contained" autoFocus>
            Aplicar Ahora
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar({ ...openSnackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setOpenSnackbar({ ...openSnackbar, open: false })}
          severity={openSnackbar.error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {openSnackbar.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SpecialRulesList;
